import React, { useState } from "react";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import IconButton from "@mui/material/IconButton";
import DialogExportExcelSpreadsheet from "../dialogs/DialogExportExcelSpreadsheet";
import { useSnackbar } from "notistack";
import { gapi } from "gapi-script";
import Snackbar from "@mui/material/Snackbar";
import Alert from '@mui/material/Alert';
import Tooltip from "@mui/material/Tooltip";
import ProjectAnalysis from "../model/ProjectAnalysis";
import { CalculateStaticAnalysisWithPeriod } from "../operations/AnalysisOperations";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useProjectStore } from "../model/ProjectStore";

//Some columns contain formulas in some cells and values in another depending on if it's a summary or not, requires some parsing 
const convertDollarSummaryFormulas = (rowValue) => {
  if ((typeof rowValue === "string" || rowValue instanceof String) && rowValue.startsWith("=")) {
    //excel handles omitting the throwaway value but spec does say it should be there
    return { formula: rowValue.slice(1)}; //SheetJS library specifically expects the '=' chopped off the front
  } else {
    return rowValue;
  }
}

const indicatorColors = {
  unStarted: 'FFD3D3D3',
  green: 'FF34A853',
  yellow: 'FFFFFF00',
  red: 'FFFF0000',
  black: 'FF000000',
}

const getIndicatorFontColor = (indicatorValue) => {
  if (indicatorValue >= 1) {
    return indicatorColors.green;
  } else if (indicatorValue >= 0.65) {
    return indicatorColors.yellow;
  } else if (indicatorValue >= 0.4) {
    return indicatorColors.red;
  } else {
    return indicatorColors.black;
  }
}

const styledReportHeader = (project, period) => {
  return [
    [{ t: "s", v: project.name }],
    [],
    [],
    []]
};

const ExportEVAToXLSX = () => {
  const project = useProjectStore();
  const [createSheetStarted, setCreateSheetStarted] = useState(false);
  const [createSheetCompleted, setCreateSheetCompleted] = useState(false);
  const [exportEvaOpen, setExportEvaOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleCreate = () => {
    setExportEvaOpen(true);
  };

  const handleExportEVAClose = () => {
    setExportEvaOpen(false);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setCreateSheetStarted(false);
    setCreateSheetCompleted(false);
  };

  const createSpreadsheet = (period) => {

    const rawAnalysis = CalculateStaticAnalysisWithPeriod(
      project,
      period,
      enqueueSnackbar,
      (result) => {
        let projectAnalysisSpecPer = ProjectAnalysis.fromJS_ToArray(
          result,
          period,
          project
        );

        createSheets(
          projectAnalysisSpecPer,
          setCreateSheetCompleted,
          period,
          enqueueSnackbar
        );
      });
  }

  const createSheets = async (projectAnalysisSpecPer,
    setCreateSheetCompleted,
    period,
    enqueueSnackbar) => {

    const workbook = new ExcelJS.Workbook();
    //for some reason by default the expander button for outline levels is at the bottom of the group instead of the top.
    const worksheet = workbook.addWorksheet('EVA', {properties: {outlineProperties : {summaryBelow : false}}});

    worksheet.views = [
  { 
    state: 'frozen', 
    xSplit: 1,      // Freezes column A
    ySplit: 5,      // Freezes row 5
  }
];
    worksheet.columns = [
      { key: 'name', width: 80 },
      { key: 'progress', width: 12 },
      { key: 'budgetSpent', width: 12 },
      { key: 'plannedProgress', width: 12 },
      { key: 'bac', width: 23 },
      { key: 'actualCost', width: 23 },
      { key: 'actualCostPeriod', width: 23 },
      { key: 'cpiCumulative', width: 12 },
      { key: 'spiCumulative', width: 12 },
      { key: 'fac', width: 23 },
      { key: 'vac', width: 23 },
      { key: 'costStatusIndicator', width: 10 },
      { key: 'schedStatusIndicator', width: 10 },
      { key: 'comment', width: 30 },

    ];

    worksheet.getCell('A2').value = {richText: [{text: 'Project: ' + project.name, font:{bold: true}}]} ;
    worksheet.getCell('A3').value = {richText: [{text: 'Period: ' + period}]} ;
    worksheet.getCell('B2').value = {
      
      richText: [
        { text: 'GREEN ', font: { bold: true, color: { argb: indicatorColors.green } } },
        { text: '= on track ● ', font: { italic: true } },
        { text: 'YELLOW ', font: { bold: true, color: { argb: indicatorColors.yellow } } },
        { text: '= slightly behind schedule or budget ● ' },
        { text: 'RED ', font: { bold: true, color: { argb: indicatorColors.red } } },
        { text: '= needs review ● '}, 
        { text: 'BLACK ', font: { bold: true } },
        { text: '= needs immediate attention ● '}, 
        { text: 'GRAY ', font: { bold: true, color: { argb: indicatorColors.unStarted } } },
        { text: '= has not started '},
      ]
    };
    worksheet.mergeCells('B2:N2');

    
    worksheet.getCell('B2').fill = {
      type: 'pattern',
      pattern: 'solid',
     fgColor: { argb: 'FF717171' }
    };

    const headerRow = worksheet.getRow(4); //this can't easily be changed, the code that generates formulas assumes it
    headerRow.values = ['Task Name', 'Progress', 'Budget Spent', 'Planned Progress', 'BAC', 'Actual Cost', 'Actual Cost Period', 'CPI Cumu.', 'SPI(T) Cumu.', 'FAC', 'VAC', 'Cost Status', 'Sched. Status', 'Comment']
    headerRow.font = { bold: true };
    headerRow.alignment = { wrapText: true }
    headerRow.eachCell({ includeEmpty: true }, function (cell, colNumber) {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
      }
    });

    let values = projectAnalysisSpecPer.map(row => {
      const sheetRow = {};
      sheetRow.name = row.displayName;
      sheetRow.progress = row.progress;
      sheetRow.budgetSpent = row.budgetSpent;
      sheetRow.plannedProgress = row.plannedProgress;
      sheetRow.bac = row.bac;
      sheetRow.actualCost = convertDollarSummaryFormulas(row.actualCost);
      sheetRow.actualCostPeriod = row.actualCostPeriod;
      sheetRow.cpiCumulative = row.cpiCumulative;
      sheetRow.spiCumulative = row.spiCumulative;
      sheetRow.fac = convertDollarSummaryFormulas(row.fac);
      sheetRow.vac = convertDollarSummaryFormulas(row.vac);
      sheetRow.costStatusIndicator = row.costStatusIndicator;
      sheetRow.schedStatusIndicator = row.schedStatusIndicator;
      sheetRow.comment = row.comment ?? "";
      sheetRow.styleInfo = row.styles;
      sheetRow.started = row.started;

      return sheetRow;
    });

    const outlineLevels = new Array(values.length).fill(0);

    for (let i = 0; i < values.length; i++) {
      const rowValues = values[i];
      const style = rowValues.styleInfo
      if (style.childCount > 0) {
        for (let j = 1; j <= style.childCount; j++ ) {
          outlineLevels[i+j]++;
        }
      }
      const row = worksheet.getRow(5 + i);
      row.outlineLevel = outlineLevels[i];
      row.font = {
                size: style.fontSize,
                bold: style.fontStyle === "bold" ? true : false,
                italic: style.fontStyle === "italic" ? true : false,};

      row.values = rowValues;
      row.getCell(1).numFmt = "@"; //always treat as string
      row.getCell(2).numFmt = "0.000%";
      row.getCell(3).numFmt = "0.000%";
      row.getCell(4).numFmt = "0.000%";
      row.getCell(5).numFmt = '"$"#,##0.00_);\\("$"#,##0.00\\)';
      row.getCell(6).numFmt = '"$"#,##0.00_);\\("$"#,##0.00\\)';
      row.getCell(7).numFmt = '"$"#,##0.00_);\\("$"#,##0.00\\)';
      row.getCell(8).numFmt = "0.00";
      row.getCell(9).numFmt = "0.00";
      row.getCell(10).numFmt = '"$"#,##0.00_);\\("$"#,##0.00\\)';
      row.getCell(11).numFmt = '"$"#,##0.00_);\\("$"#,##0.00\\)';
      row.getCell(12).font = {color: { argb: !rowValues.started ? indicatorColors.unStarted : getIndicatorFontColor(rowValues.cpiCumulative)}};
      row.getCell(13).font = {color: { argb: !rowValues.started ? indicatorColors.unStarted : getIndicatorFontColor(rowValues.spiCumulative)}};

      row.eachCell({ includeEmpty: true }, function (cell, colNumber) {
        cell.border = {
          top: { style: 'dotted' },
          left: { style: 'dotted' },
          bottom: { style: 'dotted' },
          right: { style: 'dotted' }
        };
      });
    }


    // 5. Generate Excel file binary buffer array
    const buffer = await workbook.xlsx.writeBuffer();

    // 6. Wrap buffer data in a Blob tailored for standard XLSX files
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // 7. Save file locally using file-saver
    saveAs(blob, 'EVA_Report_' + project.name.replaceAll(' ', '_') + '_Period_' + period + '.xlsx');
  };


  return (
    <div className="AddGoogleSheetButton">
      <Tooltip title="Export Spreadsheet" aria-label="Export EVA To Excel">
        <IconButton onClick={() => handleCreate()} color="inherit">
          <NoteAddIcon />
        </IconButton>
      </Tooltip>
      <DialogExportExcelSpreadsheet
        open={exportEvaOpen}
        onCancel={handleExportEVAClose}
        onSave={createSpreadsheet}
      />

    </div>
  );
}

export default ExportEVAToXLSX;
